import React, { useEffect, useState } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const LargestSmallestPurchaseTransactions = ({ zakupyData = [] }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień",
  ];

  // wartość transakcji zakupu = K_42 + K_43
  const calculateTransactionValue = (row) => {
    const k42 = parseFloat(row.K_42) || 0;
    const k43 = parseFloat(row.K_43) || 0;
    const val = k42 + k43;
    return isNaN(val) ? 0 : val;
  };

  // dostępne lata
  useEffect(() => {
    if (!zakupyData || zakupyData.length === 0) return;

    const years = [
      ...new Set(zakupyData.map((row) => parseInt(row.Rok, 10))),
    ]
      .filter((y) => !isNaN(y))
      .sort((a, b) => b - a);

    setAvailableYears(years);
    if (years.length > 0) setSelectedYear(years[0]);
  }, [zakupyData]);

  // dostępne miesiące dla wybranego roku
  useEffect(() => {
    if (!selectedYear) return;

    const months = [
      ...new Set(
        zakupyData
          .filter((row) => parseInt(row.Rok, 10) === selectedYear)
          .map((row) => parseInt(row.Miesiac, 10))
      ),
    ]
      .filter((m) => !isNaN(m))
      .sort((a, b) => a - b);

    setAvailableMonths(months);
  }, [selectedYear, zakupyData]);

  // filtrowanie po roku / miesiącu
  useEffect(() => {
    if (!selectedYear) return;

    const filtered = zakupyData.filter(
      (row) =>
        parseInt(row.Rok, 10) === selectedYear &&
        (selectedMonth === "all" ||
          parseInt(row.Miesiac, 10) === Number(selectedMonth))
    );

    setFilteredData(filtered);
  }, [selectedYear, selectedMonth, zakupyData]);

  // budowa datasetów: 5 najmniejszych, 5 największych, okolice średniej
  useEffect(() => {
    if (!filteredData || filteredData.length === 0) {
      setChartData(null);
      return;
    }

    const transactions = filteredData.map((row, index) => ({
      id: index + 1,
      value: calculateTransactionValue(row),
      date: row.DataZakupu || row.DataWystawienia || "brak daty",
      supplier: row.NazwaDostawcy || "Brak nazwy",
      document: row.DowodZakupu || "Brak numeru",
    }));

    if (transactions.length === 0) {
      setChartData(null);
      return;
    }

    const sorted = [...transactions].sort((a, b) => a.value - b.value);
    const smallest = sorted.slice(0, 5);
    const largest = sorted.slice(-5);

    const allValues = transactions.map((t) => t.value);
    const avg =
      allValues.reduce((sum, v) => sum + v, 0) / allValues.length || 0;
    const margin = avg * 0.2;
    const avgRange = sorted.filter(
      (t) => t.value >= avg - margin && t.value <= avg + margin
    );

    setChartData({
      datasets: [
        {
          label: "Najmniejsze transakcje zakupowe",
          data: smallest.map((t) => ({
            x: t.id,
            y: t.value,
            date: t.date,
            supplier: t.supplier,
            document: t.document,
          })),
          backgroundColor: "red",
          radius: 6,
        },
        {
          label: "Największe transakcje zakupowe",
          data: largest.map((t) => ({
            x: t.id,
            y: t.value,
            date: t.date,
            supplier: t.supplier,
            document: t.document,
          })),
          backgroundColor: "green",
          radius: 6,
        },
        {
          label: "Transakcje zbliżone do średniej",
          data: avgRange.map((t) => ({
            x: t.id,
            y: t.value,
            date: t.date,
            supplier: t.supplier,
            document: t.document,
          })),
          backgroundColor: "blue",
          radius: 6,
        },
      ],
    });
  }, [filteredData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Największe i najmniejsze transakcje zakupowe
        <div
          data-tooltip-id="largest-smallest-purchase-tooltip"
          style={{ cursor: "pointer" }}
        >
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="largest-smallest-purchase-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Wykres rozrzutu transakcji zakupowych</strong>
            <br />
            Pokazuje 5 największych, 5 najmniejszych
            <br />
            oraz transakcje zbliżone do średniej dla wybranego okresu.
            <br />
            Wartość liczona jako K_42 + K_43.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok: </label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label>Miesiąc: </label>
          <Select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                e.target.value === "all" ? "all" : Number(e.target.value)
              )
            }
          >
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {monthNames[month - 1]}
              </option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Scatter
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: "Transakcje (ID)" } },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Kwota (PLN)" },
                ticks: {
                  callback: (value) =>
                    Number(value).toLocaleString("pl-PL"),
                },
              },
            },
            plugins: {
              legend: { position: "top" },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y;
                    const raw = context.raw || {};

                    const lines = [
                      `Kwota: ${value.toLocaleString("pl-PL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} PLN`,
                    ];

                    if (raw.supplier) {
                      lines.push(`Dostawca: ${raw.supplier}`);
                    }
                    if (raw.document) {
                      lines.push(`Dokument: ${raw.document}`);
                    }
                    if (raw.date) {
                      lines.push(`Data: ${raw.date}`);
                    }

                    return lines;
                  },
                },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>
          Brak danych dla wybranego okresu.
        </p>
      )}
    </ChartWrapper>
  );
};

export default LargestSmallestPurchaseTransactions;
